import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { LocalCacheModel } from '../../models/local-cache.model';
import { MunicipalityFilter } from '../../models/municipality-filter.mode';
import { LocalCacheService } from '../../services/local-cache.service';
import { LocalizationService } from '../../services/localization.service';
import { NotificationService } from '../../services/notification.service';
import { ComplexMunicipalityModel } from './../../models/complex-municipality.model';
import { LocalStorageKeys } from './../../models/local-storage-keys.model';
import { MunicipalityModel } from './../../models/municipality.model';
import { FilterService } from './../../services/filter.service';
import { MunicipalityService } from './../../services/municipality.service';
import {
  AddMunicipality,
  EditMunicipality,
  FilterMunicipalities,
  FilterMunicipalitiesWithoutFiltering,
  ImportMunicipalities,
  RemoveMunicipality,
} from './../action/municipality.action';

export interface MunicipalityStateModel {
  municipalities: MunicipalityModel[];
  municipalitiesTotal: number;
  filter?: MunicipalityFilter;
}

@State<MunicipalityStateModel>({
  name: 'municipalitystate',
  defaults: {
    municipalities: [],
    municipalitiesTotal: 0,
  },
})
@Injectable()
export class MunicipalityState {
  constructor(
    private municipalityService: MunicipalityService,
    private store: Store,
    private filterService: FilterService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private localCacheService: LocalCacheService,
    private localizationService: LocalizationService
  ) {}

  @Selector()
  static municipalities(ctx: MunicipalityStateModel) {
    return ctx.municipalities;
  }

  @Selector()
  static municipalitiesTotal(ctx: MunicipalityStateModel) {
    return ctx.municipalitiesTotal;
  }

  @Action(FilterMunicipalities)
  async filterMunicipalities(
    ctx: StateContext<MunicipalityStateModel>,
    action: FilterMunicipalities
  ) {
    const data = await this.municipalityService
      .getAll(action.params, action.filter)
      .toPromise();

    console.log('munucipality data: ', data);

    if (data) {
      ctx.patchState({
        municipalities: data.data,
        municipalitiesTotal: data.total,
        filter: action.filter,
      });
    }
  }

  @Action(FilterMunicipalitiesWithoutFiltering)
  async filterMunicipalitiesWithoutFiltering(
    ctx: StateContext<MunicipalityStateModel>,
    action: FilterMunicipalitiesWithoutFiltering
  ) {
    const data = await this.municipalityService
      .getAllWithoutFiltering(action.language)
      .toPromise();

    if (data) {
      ctx.patchState({
        municipalities: data.data,
        municipalitiesTotal: data.total,
      });
    }
  }

  @Action(AddMunicipality)
  async addMunicipality(
    ctx: StateContext<MunicipalityStateModel>,
    action: AddMunicipality
  ) {
    this.localCacheService.remove(LocalStorageKeys.MunicipalitiesKey);
    this.localCacheService.remove(LocalStorageKeys.AllMunicipalitiesKey);
    this.localCacheService.remove(LocalStorageKeys.DashboardKey);

    const filter = ctx.getState().filter;
    const data = await this.municipalityService.add(action.model).toPromise();

    if (data) {
      this.store.dispatch(
        new FilterMunicipalities(
          this.filterService.generateHttpParams(),
          filter
        )
      );

      this.notificationService.info(
        this.localizationService.translate('added'),
        this.localizationService.translate(`successfully-added`)
      );
    }
  }

  @Action(EditMunicipality)
  async editMunicipality(
    ctx: StateContext<MunicipalityStateModel>,
    action: EditMunicipality
  ) {
    this.localCacheService.remove(LocalStorageKeys.MunicipalitiesKey);
    this.localCacheService.remove(LocalStorageKeys.AllMunicipalitiesKey);
    this.localCacheService.remove(LocalStorageKeys.DashboardKey);

    const filter = ctx.getState().filter;
    const data = await this.municipalityService
      .update(action.model)
      .toPromise();

    if (data) {
      this.store.dispatch(
        new FilterMunicipalities(
          this.filterService.generateHttpParams(),
          filter
        )
      );

      this.notificationService.info(
        this.localizationService.translate('edited'),
        this.localizationService.translate('successfully-edited')
      );
    }
  }

  @Action(RemoveMunicipality)
  async removeMunicipality(
    ctx: StateContext<MunicipalityStateModel>,
    action: RemoveMunicipality
  ) {
    this.localCacheService.remove(LocalStorageKeys.MunicipalitiesKey);
    this.localCacheService.remove(LocalStorageKeys.AllMunicipalitiesKey);
    this.localCacheService.remove(LocalStorageKeys.DashboardKey);

    const filter = ctx.getState().filter;
    const data = await this.municipalityService
      .remove(action.modelId)
      .toPromise();

    if (data) {
      this.store.dispatch(
        new FilterMunicipalities(
          this.filterService.generateHttpParams(),
          filter
        )
      );

      this.notificationService.info(
        this.localizationService.translate('removed'),
        this.localizationService.translate('successfully-removed')
      );
    }
  }

  @Action(ImportMunicipalities)
  async importMunicipalities(
    ctx: StateContext<MunicipalityStateModel>,
    action: ImportMunicipalities
  ) {
    this.localCacheService.remove(LocalStorageKeys.MunicipalitiesKey);
    this.localCacheService.remove(LocalStorageKeys.AllMunicipalitiesKey);
    this.localCacheService.remove(LocalStorageKeys.DashboardKey);

    const filter = ctx.getState().filter;
    const data = await this.municipalityService.import(action.data).toPromise();

    if (data) {
      this.store.dispatch(
        new FilterMunicipalities(
          this.filterService.generateHttpParams(),
          filter
        )
      );
    }
  }
}
